/**
 * 로그인 배타성 API(`PUT`·`GET`·`DELETE /v1/users/me/login`)의 wire 계약 인용과 응답 판정.
 *
 * 계약 정본은 백엔드가 소유한다 — 교차 레포 정본: dronesquare-backend capability `exclusive-login`
 * (`openspec/specs/exclusive-login/spec.md`, `401` 본문은 `openspec/specs/envelope/spec.md`).
 * 경로·메서드·밀려남 신호(status·본문·`code`)·판정표가 정본과 어긋나면 정본을 따라 이 파일을 고친다.
 *
 * 판정 재료는 status 와 `401` 본문의 `code` 둘뿐이다. 클라이언트는 토큰을 해석해 로그인을 식별하지 않는다
 * (웹 스펙 `exclusive-login`).
 */

/** main-api base(`…/v1`) 기준 상대 경로. 점유(`PUT`)·상태(`GET`)·해제(`DELETE`)가 같은 경로를 쓴다. */
export const EXCLUSIVE_LOGIN_PATH = "users/me/login";

/**
 * 같은 경로를 쓰는 점유·상태·해제의 메서드. 요청을 만드는 repository 와, 점유 대기·로그아웃 정지 중에 점유·해제
 * 요청을 거절에서 빼는 fetch 관문이 함께 읽는다. 서버의 FlightHub 진입 확인(`requestLoginStatusAsWeb`)은 이 값을
 * 읽지 않고 `fetch` 기본 메서드(`GET`)로 상태를 부른다.
 */
export const EXCLUSIVE_LOGIN_METHOD = { claim: "PUT", status: "GET", release: "DELETE" } as const;

/**
 * 밀려남 신호의 `code`. 밀려났거나 해제로 끝난 로그인의 요청은 어느 라우트에서든
 * `401 {"detail":"Unauthorized","code":"LoginSuperseded"}` 를 받는다.
 * `code` 없는 `401` 은 종전 의미(토큰 부재·만료·위조, 미정의 경로)다.
 */
export const LOGIN_SUPERSEDED_CODE = "LoginSuperseded";

/**
 * 밀려남 신호의 status. 같은 status 라도 본문에 {@link LOGIN_SUPERSEDED_CODE} 가 없으면 밀려남이 아니다.
 * 판정({@link classifyClaimResponse}·{@link classifyStatusResponse}), fetch 관문의 감지·합성 응답, FlightHub 진입
 * 서버의 감지·중계가 함께 읽는다.
 */
export const LOGIN_SUPERSEDED_STATUS = 401;

/**
 * 밀려남 신호의 본문 — 백엔드가 고정한 모양 그대로다. fetch 관문은 밀려남 모드의 합성 응답에, FlightHub 진입
 * 서버는 밀려난 로그인의 진입 응답에 싣는다(관문은 진입 응답의 이 `code` 를 보고 밀려남 처리를 시작한다).
 */
export const LOGIN_SUPERSEDED_BODY = {
  detail: "Unauthorized",
  code: LOGIN_SUPERSEDED_CODE,
} as const;

/**
 * 점유 응답의 판정 결과. 판정표는 {@link classifyClaimResponse} 를 따른다.
 * 상태 확인 결과({@link LoginStatusResult})와 같이 문자열 하나다.
 */
export type LoginClaimResult = "claimed" | "inUseElsewhere" | "superseded" | "unavailable";

/** 상태 확인 결과. `unknown` 은 화면에 아무 영향도 주지 않는 결과다(다음 주기에 다시 확인한다). */
export type LoginStatusResult = "active" | "superseded" | "unknown";

const HTTP_CONFLICT = 409;

const isSuccessStatus = (status: number) => status >= 200 && status < 300;
const isClientErrorStatus = (status: number) => status >= 400 && status < 500;

const parseJsonOrNull = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * 응답 본문이 밀려남 신호인지 판정한다. 본문은 파싱된 값이나 원문 문자열을 받는다.
 * 원문이 JSON 이 아니거나 비어 있으면 `code` 가 없는 것으로 본다.
 */
export function isLoginSupersededBody(body: unknown): boolean {
  const parsed = typeof body === "string" ? parseJsonOrNull(body) : body;
  return (
    typeof parsed === "object" &&
    parsed !== null &&
    (parsed as { code?: unknown }).code === LOGIN_SUPERSEDED_CODE
  );
}

/**
 * 점유 응답 → 결과 (design D3).
 *
 * | 응답 | 결과 |
 * |---|---|
 * | 2xx | `claimed` |
 * | `409` | `inUseElsewhere` |
 * | `401` + `code: LoginSuperseded` | `superseded` |
 * | `code` 없는 `401`, 그 밖의 4xx | `claimed` — 기능이 없는 구버전 백엔드로 본다(기능 탐지 신호) |
 * | 5xx, 그 밖의 status | `unavailable` — 닫힌 쪽으로 실패한다(진행하면 다음 요청이 경고 없이 자리를 넘겨받는다) |
 *
 * 네트워크 실패는 응답이 없으므로 호출부(repository)가 `unavailable` 로 접는다.
 */
export function classifyClaimResponse(status: number, body: unknown): LoginClaimResult {
  if (isSuccessStatus(status)) return "claimed";
  if (status === HTTP_CONFLICT) return "inUseElsewhere";
  if (status === LOGIN_SUPERSEDED_STATUS && isLoginSupersededBody(body)) return "superseded";
  if (isClientErrorStatus(status)) return "claimed";
  return "unavailable";
}

/**
 * 상태 확인 응답 → 결과. 2xx 는 `active`, 밀려남 신호만 `superseded` 다.
 * 그 밖의 결과(`code` 없는 `401`, 5xx 등)는 모두 `unknown` 이다.
 */
export function classifyStatusResponse(status: number, body: unknown): LoginStatusResult {
  if (isSuccessStatus(status)) return "active";
  if (status === LOGIN_SUPERSEDED_STATUS && isLoginSupersededBody(body)) return "superseded";
  return "unknown";
}
